import { z } from "zod";

export const SortOptionSchema = z.enum(["a-z", "z-a"]);
export type SortOption = z.infer<typeof SortOptionSchema>;

interface SortAlphabeticallyParams<T> {
  list: T[];
  selectedSort?: SortOption;
  keySelector?: (item: T) => string;
}

/**
 * Sorts an array of items alphabetically in ascending (a-z) or descending (z-a) order.
 *
 * @template T - The type of items in the array
 * @param params - The sorting parameters
 * @param params.list - The array to sort
 * @param params.selectedSort - Sort order: "a-z" (default) or "z-a"
 * @param params.keySelector - Optional function to extract the string value to sort by.
 *                              If not provided, assumes items have a `label` property.
 * @returns A new sorted array (does not mutate the original)
 *
 * @example
 * // Sort with custom key selector
 * const users = [{ name: "Charlie" }, { name: "Alice" }, { name: "Bob" }];
 * sortAlphabetically({ list: users, keySelector: (user) => user.name });
 * // Returns: [{ name: "Alice" }, { name: "Bob" }, { name: "Charlie" }]
 */
export function sortAlphabetically<T>({ list, selectedSort = "a-z", keySelector }: SortAlphabeticallyParams<T>): T[] {
  return [...list].sort((a, b) => {
    const aValue = keySelector ? keySelector(a) : (a as { label: string }).label;
    const bValue = keySelector ? keySelector(b) : (b as { label: string }).label;

    if (selectedSort === "z-a") {
      return aValue < bValue ? 1 : -1;
    }
    return aValue < bValue ? -1 : 1;
  });
}
